// ============================================================================
// BESPOKIAN Health - Checkout Page
// Multi-step checkout with shipping, prescription, and payment
// ============================================================================

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import {
  CreditCard,
  Truck,
  FileText,
  Check,
  ChevronRight,
  Shield,
  Lock,
  ArrowLeft,
  Upload,
} from 'lucide-react';

type Step = 'shipping' | 'prescription' | 'payment' | 'review';

const steps: { id: Step; label: string; icon: React.ElementType }[] = [
  { id: 'shipping', label: 'Shipping', icon: Truck },
  { id: 'prescription', label: 'Prescription', icon: FileText },
  { id: 'payment', label: 'Payment', icon: CreditCard },
  { id: 'review', label: 'Review', icon: Check },
];

export function CheckoutPage() {
  const [currentStep, setCurrentStep] = useState<Step>('shipping');
  const [formData, setFormData] = useState({
    // Shipping
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    // Prescription
    prescriptionMethod: 'transfer', // 'transfer' | 'upload' | 'doctor'
    currentPharmacy: '',
    prescriptionFile: null as File | null,
    doctorName: '',
    doctorPhone: '',
    // Payment
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: '',
    sameAsShipping: true,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const stepIndex = steps.findIndex(s => s.id === currentStep);

  const nextStep = () => {
    const nextIdx = stepIndex + 1;
    if (nextIdx < steps.length) {
      setCurrentStep(steps[nextIdx].id);
    }
  };

  const prevStep = () => {
    const prevIdx = stepIndex - 1;
    if (prevIdx >= 0) {
      setCurrentStep(steps[prevIdx].id);
    }
  };

  const handleSubmit = async () => {
    // TODO: Submit order
    alert('Order submitted! (Demo)');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-sage-50/30 flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-0.5 bg-gold-500 mx-auto mb-4" />
            <h1 className="font-display text-3xl text-navy-800 tracking-tight italic">Checkout</h1>
          </div>

          {/* Progress Steps */}
          <div className="mb-10">
            <div className="flex items-center justify-between">
              {steps.map((step, idx) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                      idx <= stepIndex
                        ? 'bg-gold-500 text-navy-800'
                        : 'bg-navy-100 text-navy-400'
                    }`}
                  >
                    <step.icon className="h-4 w-4" />
                    <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-navy-300 mx-2" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-xl border border-navy-100 shadow-card overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-400" />

            {/* Step: Shipping */}
            {currentStep === 'shipping' && (
              <div className="p-8">
                <h2 className="font-display text-xl text-navy-800 tracking-tight mb-6">Shipping Address</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">First Name</label>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Last Name</label>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Street Address</label>
                    <input
                      name="address1"
                      value={formData.address1}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Apt, Suite, etc. (optional)</label>
                    <input
                      name="address2"
                      value={formData.address2}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">City</label>
                    <input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-700 mb-1.5">State</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                        required
                      >
                        <option value="">Select</option>
                        <option value="FL">Florida</option>
                        <option value="CA">California</option>
                        <option value="TX">Texas</option>
                        <option value="NY">New York</option>
                        {/* Add more states */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-700 mb-1.5">ZIP</label>
                      <input
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Phone Number</label>
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step: Prescription */}
            {currentStep === 'prescription' && (
              <div className="p-8">
                <h2 className="font-display text-xl text-navy-800 tracking-tight mb-6">Prescription Information</h2>
                <p className="text-navy-600 mb-6">How would you like to provide your prescription?</p>

                <div className="space-y-4">
                  <label className={`block p-4 border rounded-xl cursor-pointer transition-colors ${
                    formData.prescriptionMethod === 'transfer' ? 'border-gold-500 bg-gold-50' : 'border-navy-200 hover:border-gold-300'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="prescriptionMethod"
                        value="transfer"
                        checked={formData.prescriptionMethod === 'transfer'}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-semibold text-navy-800">Transfer from another pharmacy</p>
                        <p className="text-sm text-navy-500">We'll contact your current pharmacy to transfer your prescription.</p>
                      </div>
                    </div>
                    {formData.prescriptionMethod === 'transfer' && (
                      <div className="mt-4 ml-6">
                        <label className="block text-sm font-medium text-navy-700 mb-1.5">Current Pharmacy Name & Location</label>
                        <input
                          name="currentPharmacy"
                          value={formData.currentPharmacy}
                          onChange={handleChange}
                          placeholder="e.g., CVS on Main Street"
                          className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                        />
                      </div>
                    )}
                  </label>

                  <label className={`block p-4 border rounded-xl cursor-pointer transition-colors ${
                    formData.prescriptionMethod === 'upload' ? 'border-gold-500 bg-gold-50' : 'border-navy-200 hover:border-gold-300'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="prescriptionMethod"
                        value="upload"
                        checked={formData.prescriptionMethod === 'upload'}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-semibold text-navy-800">Upload prescription</p>
                        <p className="text-sm text-navy-500">Upload a photo or PDF of your prescription.</p>
                      </div>
                    </div>
                    {formData.prescriptionMethod === 'upload' && (
                      <div className="mt-4 ml-6">
                        <div className="border-2 border-dashed border-navy-200 rounded-lg p-6 text-center">
                          <Upload className="h-8 w-8 text-navy-400 mx-auto mb-2" />
                          <p className="text-navy-600">Drop file here or click to upload</p>
                          <p className="text-xs text-navy-400 mt-1">JPG, PNG, or PDF up to 10MB</p>
                        </div>
                      </div>
                    )}
                  </label>

                  <label className={`block p-4 border rounded-xl cursor-pointer transition-colors ${
                    formData.prescriptionMethod === 'doctor' ? 'border-gold-500 bg-gold-50' : 'border-navy-200 hover:border-gold-300'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="prescriptionMethod"
                        value="doctor"
                        checked={formData.prescriptionMethod === 'doctor'}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-semibold text-navy-800">Contact my doctor</p>
                        <p className="text-sm text-navy-500">We'll reach out to your prescriber directly.</p>
                      </div>
                    </div>
                    {formData.prescriptionMethod === 'doctor' && (
                      <div className="mt-4 ml-6 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-navy-700 mb-1.5">Doctor's Name</label>
                          <input
                            name="doctorName"
                            value={formData.doctorName}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-navy-700 mb-1.5">Doctor's Phone</label>
                          <input
                            name="doctorPhone"
                            value={formData.doctorPhone}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                          />
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Step: Payment */}
            {currentStep === 'payment' && (
              <div className="p-8">
                <h2 className="font-display text-xl text-navy-800 tracking-tight mb-6">Payment Method</h2>
                <div className="flex items-center gap-2 mb-6 p-3 bg-sage-50 rounded-lg border border-sage-200">
                  <Lock className="h-4 w-4 text-sage-600" />
                  <span className="text-sm text-sage-700">Your payment info is encrypted and secure</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Card Number</label>
                    <input
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      placeholder="1234 5678 9012 3456"
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-navy-700 mb-1.5">Expiry</label>
                      <input
                        name="expiry"
                        value={formData.expiry}
                        onChange={handleChange}
                        placeholder="MM/YY"
                        className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                        required
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-navy-700 mb-1.5">CVV</label>
                      <input
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleChange}
                        placeholder="123"
                        className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                        required
                      />
                    </div>
                    <div className="col-span-1" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Name on Card</label>
                    <input
                      name="nameOnCard"
                      value={formData.nameOnCard}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                      required
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <img src="https://cdn.jsdelivr.net/gh/nxtgencat/miscellanous@main/assets/visa.svg" alt="Visa" className="h-8" />
                  <img src="https://cdn.jsdelivr.net/gh/nxtgencat/miscellanous@main/assets/mastercard.svg" alt="Mastercard" className="h-8" />
                  <img src="https://cdn.jsdelivr.net/gh/nxtgencat/miscellanous@main/assets/amex.svg" alt="Amex" className="h-8" />
                </div>
              </div>
            )}

            {/* Step: Review */}
            {currentStep === 'review' && (
              <div className="p-8">
                <h2 className="font-display text-xl text-navy-800 tracking-tight mb-6">Review Your Order</h2>

                <div className="space-y-6">
                  <div className="p-4 bg-navy-50 rounded-lg">
                    <h3 className="font-semibold text-navy-800 mb-2">Shipping Address</h3>
                    <p className="text-navy-600">
                      {formData.firstName} {formData.lastName}<br />
                      {formData.address1}<br />
                      {formData.address2 && <>{formData.address2}<br /></>}
                      {formData.city}, {formData.state} {formData.zip}
                    </p>
                  </div>

                  <div className="p-4 bg-navy-50 rounded-lg">
                    <h3 className="font-semibold text-navy-800 mb-2">Prescription</h3>
                    <p className="text-navy-600">
                      {formData.prescriptionMethod === 'transfer' && `Transfer from: ${formData.currentPharmacy}`}
                      {formData.prescriptionMethod === 'upload' && 'Prescription uploaded'}
                      {formData.prescriptionMethod === 'doctor' && `Doctor: ${formData.doctorName}`}
                    </p>
                  </div>

                  <div className="p-4 bg-navy-50 rounded-lg">
                    <h3 className="font-semibold text-navy-800 mb-2">Payment</h3>
                    <p className="text-navy-600">Card ending in {formData.cardNumber.slice(-4)}</p>
                  </div>
                </div>

                <div className="mt-8 p-4 border border-gold-200 bg-gold-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-gold-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-navy-700">
                      By placing this order, you confirm that your prescription information is accurate
                      and authorize BESPOKIAN Health to fill and ship your medications. All prescriptions are reviewed by a licensed pharmacist.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between p-6 border-t border-navy-100 bg-navy-50/50">
              {stepIndex > 0 ? (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-2 px-4 py-2 text-navy-600 hover:text-navy-800"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              ) : (
                <Link to="/cart" className="flex items-center gap-2 px-4 py-2 text-navy-600 hover:text-navy-800">
                  <ArrowLeft className="h-4 w-4" /> Back to Cart
                </Link>
              )}

              {currentStep === 'review' ? (
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-3 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors"
                >
                  Place Order <Check className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors"
                >
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
